import { initiateStepUp, submitStepUpCode, type SubmitStepUpCodeResult } from './client'
import { StepUpError, type StepUpErrorCode } from './errors'
import { translate, type TranslationKey } from '../i18n'
import { MAIL_ICON_SVG, REFRESH_ICON_SVG, SHIELD_ICON_SVG, ZUCCHETTI_LOGO_SVG } from '../icons'

export interface SubmitCodeParams {
  otpCode: string
  subjectToken: string
  signal: AbortSignal
}

export interface StepUpFormOptions {
  authority: string
  clientId: string
  accessToken: string
  locale?: string
  /**
   * Overrides how the entered code is exchanged for a step-up access token — for a confidential
   * OAuth client, whose secret this browser-side SDK can never hold. When set, this replaces the
   * SDK's own call to `POST {authority}connect/token`; the app is expected to call its own
   * backend, which makes that request server-side with the client_secret attached. Discovery
   * (`POST {authority}step-up/initiate`) is unaffected — it's authenticated by the user's bearer
   * token, not the client, so it already works for a confidential client with no change needed.
   *
   * Resolve with the same shape the built-in call would have returned. To reject, throw/reject
   * with a `StepUpError` using the code you want the SDK to react to — 'invalid_code' gets the
   * same inline retry as a wrong code today, any other code gets the same terminal message and
   * immediate Promise rejection. Anything else thrown (a plain Error, a non-StepUpError value)
   * is treated as 'network_error', same fallback already used for an unexpected internal failure.
   */
  onSubmitCode?: (params: SubmitCodeParams) => Promise<StepUpResult>
}

export type StepUpResult = SubmitStepUpCodeResult

export interface StepUpFormHandle {
  /**
   * Cancels the flow from outside the form — the modal shell's X, Esc and backdrop click.
   * Goes through the form's own settle guard (so it can't reject behind the form's back while
   * an outcome is already being delivered) and aborts any in-flight request, so a verification
   * already on the wire can't come back and resolve a token nobody is listening for. No-op once
   * the flow has settled.
   */
  requestCancel: () => void
}

const CODE_MAX_LENGTH = 6

const TERMINAL_ERROR_KEYS: Record<
  Exclude<StepUpErrorCode, 'invalid_code' | 'cancelled' | 'already_open'>,
  TranslationKey
> = {
  rate_limited: 'StepUp.Error.RateLimited',
  invalid_access_token: 'StepUp.Error.InvalidAccessToken',
  not_enabled: 'StepUp.Error.NotEnabled',
  network_error: 'StepUp.Error.Network',
}

/**
 * Mode-agnostic: builds (and fully owns) the step-up form inside `container` — discovery,
 * the totp/email-otp variant, resend/cooldown, submit — and reports the outcome via three
 * callbacks. Doesn't know or care whether it's inside a modal dialog or inline in the host
 * app's own layout; that's the two wrappers' job (modal.ts, inline.ts).
 *
 * `onResolve`/`onReject` settle the Promise the wrapper returns to the caller — they fire the
 * instant an outcome is known, terminal or not, so the app's own await/catch reacts right away.
 * `onDismiss` is separate: it's when the *UI* should actually be torn down. For a cancel it
 * fires together with the reject (there's nothing left to show). For a terminal error
 * (rate-limit, invalid token, ...) it does NOT fire immediately — the form is replaced by an
 * explanatory message with its own "Close", so the user isn't left looking at a screen that
 * just vanished with no explanation; onDismiss only fires once they dismiss that message.
 *
 * The returned handle is how the shell asks for a cancel instead of settling the Promise itself.
 */
export function buildStepUpForm(
  container: HTMLElement,
  options: StepUpFormOptions,
  onResolve: (result: StepUpResult) => void,
  onReject: (error: StepUpError) => void,
  onDismiss: () => void,
): StepUpFormHandle {
  let settled = false
  let busy = false
  let cooldownRemaining = 0
  let cooldownTimer: ReturnType<typeof setInterval> | undefined
  const abortController = new AbortController()

  const stopCooldown = (): void => {
    cooldownRemaining = 0
    if (cooldownTimer !== undefined) {
      clearInterval(cooldownTimer)
      cooldownTimer = undefined
    }
  }

  // Single gate for every outcome: whoever gets here first owns the result, and any request
  // still in flight is abandoned so its late response can't try to settle a second time.
  const claimOutcome = (): boolean => {
    if (settled) {
      return false
    }
    settled = true
    stopCooldown()
    abortController.abort()
    return true
  }

  const resolveAndDismiss = (result: StepUpResult): void => {
    if (!claimOutcome()) {
      return
    }
    onResolve(result)
    onDismiss()
  }

  const cancelAndDismiss = (): void => {
    if (!claimOutcome()) {
      return
    }
    onReject(new StepUpError('cancelled', 'The user cancelled the step-up form.'))
    onDismiss()
  }

  const rejectTerminal = (error: StepUpError): void => {
    if (!claimOutcome()) {
      return
    }
    onReject(error)
    renderTerminal(error)
  }

  const asStepUpError = (err: unknown): StepUpError =>
    err instanceof StepUpError ? err : new StepUpError('network_error', String(err))

  const renderMessage = (text: string): void => {
    container.innerHTML = ''
    const message = document.createElement('p')
    message.className = 'zid-stepup-terminal'
    message.textContent = text
    container.appendChild(message)
  }

  const buildBrandHeader = (): HTMLParagraphElement => {
    const brand = document.createElement('p')
    brand.className = 'zid-stepup-brand'
    brand.textContent = translate('StepUp.Brand', options.locale)
    return brand
  }

  const buildMethodIcon = (method: 'totp' | 'email_otp'): HTMLDivElement => {
    const circle = document.createElement('div')
    circle.className = 'zid-stepup-icon-circle'
    const icon = document.createElement('span')
    icon.className = 'zid-stepup-icon'
    icon.innerHTML = method === 'totp' ? SHIELD_ICON_SVG : MAIL_ICON_SVG
    circle.appendChild(icon)
    return circle
  }

  // Divider + logo/copyright footer, appended at the bottom of every card state (form,
  // terminal error) so the chrome stays consistent regardless of where the flow currently is.
  const appendFooter = (wrapper: HTMLElement): void => {
    const divider = document.createElement('hr')
    divider.className = 'zid-stepup-divider'

    const footer = document.createElement('div')
    footer.className = 'zid-stepup-footer'

    const logo = document.createElement('span')
    logo.className = 'zid-stepup-footer-logo'
    logo.innerHTML = ZUCCHETTI_LOGO_SVG

    const copy = document.createElement('p')
    copy.className = 'zid-stepup-footer-copy'
    copy.textContent = `© ${new Date().getFullYear()} - ZIdentity`

    footer.append(logo, copy)
    wrapper.append(divider, footer)
  }

  function renderTerminal(error: StepUpError): void {
    container.innerHTML = ''

    const wrapper = document.createElement('div')
    wrapper.className = 'zid-stepup-form'

    const title = document.createElement('p')
    title.className = 'zid-stepup-title'
    title.textContent = translate('StepUp.Title', options.locale)

    const message = document.createElement('p')
    message.className = 'zid-stepup-terminal'
    const key =
      error.code === 'cancelled' || error.code === 'invalid_code' || error.code === 'already_open'
        ? undefined
        : TERMINAL_ERROR_KEYS[error.code]
    message.textContent = key ? translate(key, options.locale) : translate('StepUp.Error.Network', options.locale)

    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'zid-stepup-cancel'
    close.textContent = translate('StepUp.Close', options.locale)
    close.addEventListener('click', onDismiss)

    wrapper.append(buildBrandHeader(), title, message, close)
    appendFooter(wrapper)
    container.appendChild(wrapper)
    close.focus()
  }

  function renderForm(method: 'totp' | 'email_otp', resendCooldownSeconds: number): void {
    container.innerHTML = ''

    const form = document.createElement('form')
    form.className = 'zid-stepup-form'
    form.noValidate = true

    const title = document.createElement('p')
    title.className = 'zid-stepup-title'
    title.textContent = translate('StepUp.Title', options.locale)

    const prompt = document.createElement('p')
    prompt.className = 'zid-stepup-prompt'
    prompt.textContent = translate(
      method === 'totp' ? 'StepUp.Totp.Prompt' : 'StepUp.EmailOtp.Prompt',
      options.locale,
    )

    const field = document.createElement('div')
    field.className = 'zid-stepup-field'

    const codeLabelText = translate(
      method === 'totp' ? 'StepUp.Totp.CodeLabel' : 'StepUp.EmailOtp.CodeLabel',
      options.locale,
    )

    // Visually hidden rather than removed: the input's accessible name still comes from a
    // real, programmatically-associated <label>; the placeholder (see below) is what a
    // sighted user actually reads. A fixed id is safe even with multiple forms on the same
    // page — each render lives in its own Shadow DOM, which is its own id-scoping boundary.
    const inputId = 'zid-stepup-code-input'
    const label = document.createElement('label')
    label.className = 'zid-stepup-label zid-stepup-label--visually-hidden'
    label.htmlFor = inputId
    label.textContent = codeLabelText

    const inputWrap = document.createElement('div')
    inputWrap.className = 'zid-stepup-input-wrap'

    const inputIcon = document.createElement('span')
    inputIcon.className = 'zid-stepup-input-icon'
    inputIcon.innerHTML = SHIELD_ICON_SVG

    const input = document.createElement('input')
    input.id = inputId
    input.className = 'zid-stepup-input'
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'one-time-code'
    input.maxLength = CODE_MAX_LENGTH
    input.placeholder = codeLabelText

    inputWrap.append(inputIcon, input)

    const errorEl = document.createElement('p')
    errorEl.className = 'zid-stepup-error'
    errorEl.style.display = 'none'

    field.append(label, inputWrap, errorEl)
    form.append(buildBrandHeader(), buildMethodIcon(method), title, prompt, field)

    let resendButton: HTMLButtonElement | undefined
    if (method === 'email_otp') {
      const nocodeRow = document.createElement('div')
      nocodeRow.className = 'zid-stepup-nocode'

      const nocodeLabel = document.createElement('span')
      nocodeLabel.textContent = translate('StepUp.EmailOtp.NoCode', options.locale)

      resendButton = document.createElement('button')
      resendButton.type = 'button'
      resendButton.className = 'zid-stepup-resend'

      const resendIcon = document.createElement('span')
      resendIcon.className = 'zid-stepup-resend-icon'
      resendIcon.innerHTML = REFRESH_ICON_SVG

      const resendLabel = document.createElement('span')
      resendLabel.className = 'zid-stepup-resend-label'

      resendButton.append(resendIcon, resendLabel)
      nocodeRow.append(nocodeLabel, resendButton)
      form.appendChild(nocodeRow)

      startCooldown(resendButton, resendCooldownSeconds)

      const button = resendButton
      button.addEventListener('click', () => {
        void handleResend(button)
      })
    }

    const actions = document.createElement('div')
    actions.className = 'zid-stepup-actions'

    const submit = document.createElement('button')
    submit.type = 'submit'
    submit.className = 'zid-stepup-submit'
    submit.textContent = translate('StepUp.Submit', options.locale)

    const cancel = document.createElement('button')
    cancel.type = 'button'
    cancel.className = 'zid-stepup-cancel'
    cancel.textContent = translate('StepUp.Cancel', options.locale)
    cancel.addEventListener('click', cancelAndDismiss)

    actions.append(submit, cancel)
    form.appendChild(actions)
    appendFooter(form)

    container.appendChild(form)
    input.focus()

    function setBusy(nextBusy: boolean): void {
      busy = nextBusy
      submit.disabled = busy
      input.disabled = busy
      // Cancel and resend go quiet too while a verification is on the wire: abandoning here
      // would throw away a step-up token the server may have already issued — and burned the
      // one-time code for. The modal shell's X/Esc/backdrop stay available as the escape hatch,
      // and they abort the request before cancelling.
      cancel.disabled = busy
      if (resendButton) {
        resendButton.disabled = busy || cooldownRemaining > 0
      }
      submit.textContent = translate(busy ? 'StepUp.Submitting' : 'StepUp.Submit', options.locale)
    }

    async function handleResend(button: HTMLButtonElement): Promise<void> {
      // Goes through the same busy gate as submit: initiateStepUp() shares the form's
      // AbortController with submitStepUpCode(), so a resend failure that aborts here must not
      // be allowed to race a submit that's concurrently on the wire (see setBusy above).
      setBusy(true)
      try {
        const { resendCooldownSeconds } = await initiateStepUp({
          authority: options.authority,
          accessToken: options.accessToken,
          signal: abortController.signal,
        })
        startCooldown(button, resendCooldownSeconds ?? 0)
      } catch (err) {
        if (abortController.signal.aborted) {
          return
        }
        rejectTerminal(asStepUpError(err))
      } finally {
        setBusy(false)
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      const code = input.value.trim()
      if (!code) {
        errorEl.textContent = translate('StepUp.Error.InvalidCode', options.locale)
        errorEl.style.display = 'block'
        return
      }

      void (async () => {
        setBusy(true)
        errorEl.style.display = 'none'
        try {
          const result = options.onSubmitCode
            ? await options.onSubmitCode({
                otpCode: code,
                subjectToken: options.accessToken,
                signal: abortController.signal,
              })
            : await submitStepUpCode({
                authority: options.authority,
                clientId: options.clientId,
                subjectToken: options.accessToken,
                otpCode: code,
                signal: abortController.signal,
              })
          resolveAndDismiss(result)
        } catch (err) {
          if (abortController.signal.aborted) {
            return
          }
          const error = asStepUpError(err)
          if (error.code === 'invalid_code') {
            errorEl.textContent = translate('StepUp.Error.InvalidCode', options.locale)
            errorEl.style.display = 'block'
            input.value = ''
            setBusy(false)
            input.focus()
            return
          }
          rejectTerminal(error)
        }
      })()
    })
  }

  function startCooldown(button: HTMLButtonElement, seconds: number): void {
    stopCooldown()
    cooldownRemaining = seconds
    // The label lives in its own span so the icon span next to it survives repeated updates —
    // button.textContent would wipe both on every tick.
    const labelSpan = button.querySelector<HTMLSpanElement>('.zid-stepup-resend-label')

    const updateLabel = (): void => {
      button.disabled = cooldownRemaining > 0 || busy
      const text =
        cooldownRemaining > 0
          ? `${translate('StepUp.EmailOtp.Resend', options.locale)} (${cooldownRemaining}s)`
          : translate('StepUp.EmailOtp.Resend', options.locale)
      if (labelSpan) {
        labelSpan.textContent = text
      } else {
        button.textContent = text
      }
    }

    updateLabel()
    if (cooldownRemaining > 0) {
      cooldownTimer = setInterval(() => {
        cooldownRemaining -= 1
        updateLabel()
        if (cooldownRemaining <= 0) {
          stopCooldown()
        }
      }, 1000)
    }
  }

  renderMessage(translate('StepUp.Discovering', options.locale))
  void (async () => {
    try {
      const { method, resendCooldownSeconds } = await initiateStepUp({
        authority: options.authority,
        accessToken: options.accessToken,
        signal: abortController.signal,
      })
      if (abortController.signal.aborted) {
        return
      }
      renderForm(method, resendCooldownSeconds ?? 0)
    } catch (err) {
      if (abortController.signal.aborted) {
        return
      }
      rejectTerminal(asStepUpError(err))
    }
  })()

  return { requestCancel: cancelAndDismiss }
}
